import React, { useState, useEffect } from "react";
import { createSocialAd } from "../../../api";
import { useToast } from "../../../hooks/use-toast";
import { X, Loader2, Rocket, Target, Users, PoundSterling } from "lucide-react";

const inputCls = "w-full px-3 py-2 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm";
const labelCls = "text-xs font-semibold uppercase tracking-wide text-gray-500 block mb-1";

const OBJECTIVES = [
  { v: "traffic", l: "Traffic — send people to your website" },
  { v: "leads", l: "Leads — get enquiries" },
  { v: "engagement", l: "Engagement — likes, comments, shares" },
  { v: "awareness", l: "Awareness — reach as many people as possible" },
];

const AdModal = ({ post, publishedPosts, onClose, onSaved }) => {
  const [postId, setPostId] = useState(post?.id || (publishedPosts?.[0]?.id || ""));
  const [name, setName] = useState(post ? `Boost — ${(post.carTitle || post.caption || "Post").slice(0, 40)}` : "");
  const [objective, setObjective] = useState("traffic");
  const [locations, setLocations] = useState("United Kingdom");
  const [ageMin, setAgeMin] = useState(25);
  const [ageMax, setAgeMax] = useState(65);
  const [interests, setInterests] = useState("Classic cars, Sports cars, Car collecting");
  const [dailyBudget, setDailyBudget] = useState(15);
  const [durationDays, setDurationDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!post && publishedPosts?.length) {
      const p = publishedPosts.find((x) => x.id === postId) || publishedPosts[0];
      if (p && !name) setName(`Boost — ${(p.carTitle || p.caption || "Post").slice(0, 40)}`);
    }
  }, [postId]); // eslint-disable-line

  const totalBudget = (Number(dailyBudget) || 0) * (Number(durationDays) || 0);
  const estReach = Math.round(totalBudget * 280);

  const save = async () => {
    if (!postId) return toast({ title: "Pick a post to boost", variant: "destructive" });
    if (!name.trim()) return toast({ title: "Name your ad", variant: "destructive" });
    if (Number(dailyBudget) <= 0 || Number(durationDays) <= 0) return toast({ title: "Budget and duration must be greater than 0", variant: "destructive" });
    setBusy(true);
    try {
      await createSocialAd({
        postId, name, objective,
        locations: locations.split(",").map((s) => s.trim()).filter(Boolean),
        ageMin: Number(ageMin), ageMax: Number(ageMax),
        interests: interests.split(",").map((s) => s.trim()).filter(Boolean),
        dailyBudget: Number(dailyBudget), durationDays: Number(durationDays),
        status: "active",
      });
      toast({ title: "Ad created (demo)", description: "It's now running in your Ads list." });
      onSaved();
    } catch (e) { toast({ title: "Could not create ad", variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="ad-modal">
      <div className="bg-white w-full max-w-lg my-8 border border-black/10">
        <div className="flex items-center justify-between px-6 py-4 gpm-bg-green">
          <h3 className="font-serif-display text-lg font-bold gpm-text-cream flex items-center gap-2"><Rocket size={18} /> Turn post into an ad</h3>
          <button onClick={onClose} data-testid="ad-close" className="gpm-text-cream hover:gpm-text-gold"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-5">
          {!post && (
            <div>
              <label className={labelCls}>Post to promote</label>
              <select data-testid="ad-post-select" className={inputCls} value={postId} onChange={(e) => setPostId(e.target.value)}>
                {(publishedPosts || []).map((p) => <option key={p.id} value={p.id}>{(p.carTitle || p.caption || "Post").slice(0, 50)}</option>)}
              </select>
              {!publishedPosts?.length && <p className="text-xs text-red-600 mt-1">Publish a post first to promote it.</p>}
            </div>
          )}

          <div><label className={labelCls}>Ad name</label><input data-testid="ad-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>

          <div>
            <label className={labelCls}><Target size={12} className="inline mr-1" /> Goal</label>
            <select data-testid="ad-objective" className={inputCls} value={objective} onChange={(e) => setObjective(e.target.value)}>
              {OBJECTIVES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
            </select>
          </div>

          <div>
            <label className={labelCls}><Users size={12} className="inline mr-1" /> Audience locations (comma separated)</label>
            <input data-testid="ad-locations" className={inputCls} value={locations} onChange={(e) => setLocations(e.target.value)} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Age from</label><input data-testid="ad-age-min" type="number" className={inputCls} value={ageMin} onChange={(e) => setAgeMin(e.target.value)} /></div>
            <div><label className={labelCls}>Age to</label><input data-testid="ad-age-max" type="number" className={inputCls} value={ageMax} onChange={(e) => setAgeMax(e.target.value)} /></div>
          </div>

          <div><label className={labelCls}>Interests (comma separated)</label><input data-testid="ad-interests" className={inputCls} value={interests} onChange={(e) => setInterests(e.target.value)} /></div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}><PoundSterling size={12} className="inline mr-1" /> Daily budget (£)</label><input data-testid="ad-budget" type="number" min="1" className={inputCls} value={dailyBudget} onChange={(e) => setDailyBudget(e.target.value)} /></div>
            <div><label className={labelCls}>Run for (days)</label><input data-testid="ad-duration" type="number" min="1" className={inputCls} value={durationDays} onChange={(e) => setDurationDays(e.target.value)} /></div>
          </div>

          <div className="bg-[#f4f2ea] border border-black/10 p-4 text-sm flex justify-between">
            <div><div className="text-xs text-gray-500 uppercase tracking-wide">Total budget</div><div className="text-lg font-bold gpm-text-green">£{totalBudget.toLocaleString()}</div></div>
            <div className="text-right"><div className="text-xs text-gray-500 uppercase tracking-wide">Est. reach</div><div className="text-lg font-bold" style={{ color: "var(--gpm-gold)" }}>{estReach.toLocaleString()}</div></div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-black/10 bg-gray-50">
          <button onClick={onClose} className="px-4 py-2.5 border border-black/20 text-sm font-semibold">Cancel</button>
          <button data-testid="ad-launch" onClick={save} disabled={busy || (!post && !publishedPosts?.length)} className="flex items-center gap-2 px-5 py-2.5 gpm-text-cream text-sm font-semibold disabled:opacity-60" style={{ backgroundColor: "var(--gpm-green)" }}>{busy ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />} Launch ad</button>
        </div>
      </div>
    </div>
  );
};

export default AdModal;
